import React, { useCallback } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import {
    Grid,
    Checkbox,
    Paper,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Typography,
    Button,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import { ELEMENT_PATHS } from 'yeself-common/enum/ModelEnum';
import { DisabledLocationPaths } from 'yeself-common/typings/application/Application';

import { updateDetailSettings } from 'Console/data/applicationDetail/settings/SettingsAction';

import { selectApplicationSelectorDisabledLocationPaths } from 'Console/data/applicationDetail/settings/SettingsSelector';

const styles = (theme) => ({
    root: {
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
    },
    title: {
        display: 'inline-block',
        'font-weight': 'bold',
        'padding-bottom': theme.typography.pxToRem(20),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        paddingBottom: theme.typography.pxToRem(34),
    },
    btn: {
        marginBottom: theme.typography.pxToRem(65),
        marginTop: theme.typography.pxToRem(20),
    },
    paper: {
        width: '100%',
        [theme.breakpoints.down('xs')]: {
            width: window.innerWidth * 0.7,
            overflow: 'scroll',
        },
    },
    table: {
        width: '100%',
    },
    tableItem: {},
});

const SelectorDefaults = ({ classes }): React.ReactElement => {
    const dispatch = useDispatch();

    const disabledLocationPathsInitial = useSelector(selectApplicationSelectorDisabledLocationPaths);

    const [disabledLocationPaths, setDisabledLocationPaths] = React.useState<DisabledLocationPaths>(
        disabledLocationPathsInitial
    );

    const handleCheck = useCallback(
        (event) => {
            const { id, checked: isChecked } = event.target;
            const updatedData = isChecked
                ? [...disabledLocationPaths, id]
                : disabledLocationPaths.filter((item) => item !== id);

            setDisabledLocationPaths(updatedData);
        },
        [disabledLocationPaths]
    );

    const handleSaveSettings = useCallback(() => {
        dispatch(
            updateDetailSettings(
                {
                    'selectorSystem.disabledLocationPaths': disabledLocationPaths,
                },
                {
                    success: (
                        <FormattedMessage
                            id="Notification.save"
                            defaultMessage="Your settings were saved"
                        />
                    ),

                    fail: (
                        <FormattedMessage
                            id="Notification.fail"
                            defaultMessage="Unable to save, something went wrong"
                        />
                    ),
                }
            )
        );
    }, [dispatch, disabledLocationPaths]);

    const locationPaths = {
        [ELEMENT_PATHS.XPATH]: (
            <FormattedMessage id="ElementLocation.field.path.xpath" defaultMessage="Element XPath" />
        ),
        [ELEMENT_PATHS.RELATIVE]: (
            <FormattedMessage
                id="ElementLocation.field.cssPath.relative"
                defaultMessage="Element CSS relative path"
            />
        ),
        [ELEMENT_PATHS.XPATH_LIMITED]: (
            <FormattedMessage
                id="ElementLocation.field.path.xpathLimited"
                defaultMessage="XPath Element - limited"
            />
        ),
        [ELEMENT_PATHS.RELATIVE_LIMITED]: (
            <FormattedMessage
                id="ElementLocation.field.cssPath.relativeLimited"
                defaultMessage="Element CSS relative limited path"
            />
        ),
        [ELEMENT_PATHS.EXPRESSED]: (
            <FormattedMessage
                id="ElementLocation.field.cssPath.expressed"
                defaultMessage="Element CSS expressed path"
            />
        ),
        [ELEMENT_PATHS.CLASS_ONLY]: (
            <FormattedMessage
                id="ElementLocation.field.cssPath.classOnly"
                defaultMessage="Element CSS class-only path"
            />
        ),
        [ELEMENT_PATHS.TAG_ONLY]: (
            <FormattedMessage
                id="ElementLocation.field.cssPath.tagOnly"
                defaultMessage="Element CSS tag-only path"
            />
        ),
    };

    return (
        <Grid container className={classes.root}>
            <Grid item>
                <Typography variant="subtitle1" className={classes.title}>
                    <FormattedMessage
                        id="Settings.selectorDefaults.title"
                        defaultMessage="Element Selector Overrides"
                    />
                </Typography>
            </Grid>

            <Grid item>
                <Typography variant="body1" className={classes.message}>
                    <FormattedMessage
                        id="Settings.selectorDefaults.message"
                        defaultMessage="Here you can set which Element Locations you want to <strong>disable</strong> by default during selector creation. To <strong>disable</strong>, tick the required selectors from the list below. Changes here will be reflected in new selections only.  The defaults will be applied to all places where you can pick an element."
                        values={{
                            strong: (section) => <strong>{section}</strong>,
                        }}
                    />
                </Typography>
            </Grid>

            <Grid container id="selector-defaults">
                <Paper className={classes.paper}>
                    <Table className={classes.table}>
                        <TableBody>
                            {Object.entries(locationPaths).map(([pathName, element]) => (
                                <TableRow key={`selector-defaults-row-${pathName}`}>
                                    <TableCell>{element}</TableCell>
                                    <TableCell
                                        id={`selector-defaults-item-${pathName}`}
                                        className={classes.tableItem}
                                    >
                                        <Checkbox
                                            id={pathName}
                                            color="primary"
                                            onChange={handleCheck}
                                            checked={disabledLocationPaths.includes(
                                                pathName as typeof ELEMENT_PATHS[keyof typeof ELEMENT_PATHS]
                                            )}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            </Grid>

            <Grid container alignItems="center">
                <Typography variant="button">
                    <Button
                        id="yeself-button"
                        variant="contained"
                        color="primary"
                        onClick={handleSaveSettings}
                        className={classes.btn}
                    >
                        <FormattedMessage id="Buttons.save" />
                    </Button>
                </Typography>
            </Grid>
        </Grid>
    );
};
export default withStyles(styles)(SelectorDefaults);
